import { DateTime } from 'luxon';
import { elapsedSinceIsoMs, parseRevGeneration } from './sync-rev-and-time-utils';

// One decider per sync diagnostic: whether a drift retry healed, whether a write stranded
// across a session boundary should be reported, whether the push has stalled, whether the
// pull needs a catch-up, whether the real-time listener may restart again, whether the
// foreground health tick should run, and whether a wrong Offline should be re-probed.
//
// Each is a pure function so the condition it decides can be unit-tested on its own. They
// live outside DatabaseWrapper because that class imports pouchdb-browser, which references
// `self` and cannot be loaded under node. DatabaseWrapper delegates to them.

/** one doc that a previous integrity pass failed to heal and this pass no longer does */
export interface DriftHealedDoc {
  docId: string;
  /** milliseconds between the doc's FIRST failed heal and now */
  retryAfterMs: number;
}

/** outcome of comparing this pass's failed heal docs with the previous pass's (#4010 B4) */
export interface DriftRetryOutcome {
  /** docs that failed on an earlier pass and are no longer failing (healed or no longer drifted) */
  healed: DriftHealedDoc[];
  /** serialized `{ docId: firstFailIso }` map to store for the next pass */
  nextFailedJson: string;
}

/**
 * Compare the docs that failed healing on a previous integrity pass with this pass's failures,
 * so a "healed on retry after N minutes" follow-up can be reported (#4010 B4 — turns the
 * "the 3ZD residual is transient and self-heals next tick" belief into measured fact).
 * A doc counts as healed when it was in the stored map and is NOT failing now — either its
 * force-push succeeded, or it no longer drifts at all. Docs still failing keep their FIRST
 * failure timestamp so the retry age keeps accumulating across passes.
 */
export function computeDriftRetryOutcome(
  prevFailedJson: string | undefined,
  currentFailedIds: string[],
  nowMs: number,
  nowIso: string,
): DriftRetryOutcome {
  let prevFailed: Record<string, string> = {};
  if (prevFailedJson) {
    try {
      prevFailed = JSON.parse(prevFailedJson);
    } catch {
      // expected: a malformed sessionStorage value from an older build — start fresh rather
      // than wedging every future pass on unparseable state
      prevFailed = {};
    }
  }

  const healed: DriftHealedDoc[] = [];
  for (const [docId, firstFailIso] of Object.entries(prevFailed)) {
    if (!currentFailedIds.includes(docId)) {
      healed.push({ docId, retryAfterMs: elapsedSinceIsoMs(firstFailIso, nowMs) });
    }
  }

  const nextFailed: Record<string, string> = {};
  for (const docId of currentFailedIds) {
    // keep the FIRST failure time for docs that keep failing, stamp new failures with now
    nextFailed[docId] = prevFailed[docId] ?? nowIso;
  }

  return { healed, nextFailedJson: JSON.stringify(nextFailed) };
}

/**
 * Decide whether the PREVIOUS session ended with an unflushed local write (#4010 B3 — closes
 * the documented Phase-1 gap where the stale-unflushed diagnostic was session-scoped: a write
 * left pending when the app was killed was invisible to the next session). Reads the
 * localStorage twins of the session-scoped tracking flags. Reports only when a persisted
 * change exists, is older than `minAgeMs` (skips quick-reload races), and was never acked by
 * a persisted sync timestamp at or after it.
 */
export function decidePersistedUnflushedReport(
  persistedChangeIso: string | undefined,
  persistedSyncIso: string | undefined,
  nowMs: number,
  minAgeMs = 30_000,
): { shouldReport: boolean, ageMs: number } {
  if (!persistedChangeIso) return { shouldReport: false, ageMs: 0 };
  const changeMs = DateTime.fromISO(persistedChangeIso).toMillis();
  if (!Number.isFinite(changeMs)) return { shouldReport: false, ageMs: 0 };
  const ageMs = Math.max(0, nowMs - changeMs);
  if (ageMs < minAgeMs) return { shouldReport: false, ageMs };
  const syncMs = persistedSyncIso ? DateTime.fromISO(persistedSyncIso).toMillis() : NaN;
  // if no sync ack was persisted, or the last ack predates the change, the write never flushed
  const isUnflushed = !Number.isFinite(syncMs) || syncMs < changeMs;
  return { shouldReport: isUnflushed, ageMs };
}

// ---------------------------------------------------------------------------
// df-ed6d17c8ec6f — detect a live-sync PUSH that keeps failing.
//
// A persistently-failing push is otherwise invisible to the app. `.on('error')`
// sets lastSyncError and the next clean `.on('paused')` nulls it, so `retry: true`
// against a failing push leaves lastSyncError reading null most of the time and any
// observer sampling it sees a healthy sync. The existing stale-unflushed diagnostic
// does not cover it either: a working PULL bumps lastUserDbSyncTime_utc for EITHER
// direction, which masks a push that never lands. The signal that is not masked is
// a PUSH ack — only a push proves our own writes reached the server.
// See design-docs/sync-integrity.md.
// ---------------------------------------------------------------------------

/**
 * How long the live-sync push may be continuously failing — while the app is
 * online and foregrounded — before it is reported as a problem
 * (df-ed6d17c8ec6f).
 *
 * Five minutes rather than the stale-unflushed detector's 30s: this must not fire
 * for an ordinary brief offline period, only for a push that is genuinely not
 * landing. The window is measured over a CONTINUOUSLY healthy stretch — see
 * {@link decidePushStallReport}.
 */
export const PUSH_STALL_MIN_MS = 5 * 60 * 1000;

/** the sessionStorage key holding the push-stall "stuck since" clock (epoch ms as a string) */
export const PUSH_STUCK_SINCE_STORAGE_KEY = 'pushStuckSince_ms';

/** the documents a push-stall clock reset listener is already installed on */
const pushStallClockResetInstalledOn = new WeakSet<object>();

/**
 * Clear the push-stall "stuck since" clock every time the page becomes hidden
 * (df-280a5f714662). The clock is otherwise only sampled on a live-sync `paused`, and
 * none fires while iOS has the app suspended, so a clock started before the app went
 * to the background survived the whole suspension and reported on resume. Clearing it
 * here makes the window really a continuously foregrounded stretch.
 *
 * Installs at most one listener per document, so every DatabaseWrapper may call it.
 */
export function installPushStallClockResetOnHide(
  doc: { readonly hidden: boolean, addEventListener(type: 'visibilitychange', listener: () => void): void },
  storage: Pick<Storage, 'removeItem'>,
): void {
  if (pushStallClockResetInstalledOn.has(doc)) return;
  pushStallClockResetInstalledOn.add(doc);
  doc.addEventListener('visibilitychange', () => {
    // if the page went to the background, the stretch is no longer continuous
    if (doc.hidden) {
      storage.removeItem(PUSH_STUCK_SINCE_STORAGE_KEY);
    }
  });
}

/** what {@link isSyncDiagnosticEnvironmentHealthy} needs to know about the device right now */
export interface SyncDiagnosticEnvironment {
  /** `connectionState === ConnectionState.Online` — handed over as a boolean because that
   *  enum's module imports Angular and cannot be loaded under node */
  connectionStateIsOnline: boolean;
  /** `document.hidden` — the app is backgrounded, and iOS suspends its networking */
  documentHidden: boolean;
  /** the live sync failed with a network-shaped error in the last 30 s */
  recentNetworkSyncError: boolean;
  /**
   * `DatabaseState === Offline`: the sync error / denied / auth handlers have already
   * published Offline, so a pending write is a known wait rather than a silent problem.
   * Optional because not every diagnostic consults it: the checkpoint-drift report only
   * runs once the database is Ready, and the pull catch-up signal has never looked at it.
   * Leave it out to skip that term.
   */
  databaseIsOffline?: boolean;
}

/**
 * True when a sync anomaly seen right now is genuine rather than an ordinary wait: the
 * connection is online, the app is in the foreground, no network-shaped sync error just
 * happened, and (when supplied) the database is not already Offline. Every sync diagnostic
 * asks this before it reports to Sentry — the stale-unflushed change, the push stall, the
 * checkpoint drift and the pull catch-up — so what "healthy" means is decided here once
 * (df-62be0356dad1).
 *
 * Online is tested as `=== Online`, not `!== Offline`, so a future third connection state
 * reads as unhealthy at every site at once.
 */
export function isSyncDiagnosticEnvironmentHealthy(env: SyncDiagnosticEnvironment): boolean {
  if (env.databaseIsOffline === true) return false;
  if (!env.connectionStateIsOnline) return false;
  if (env.documentHidden) return false;
  if (env.recentNetworkSyncError) return false;
  return true;
}

/**
 * True when the user database has an own-device local write that no PUSH has
 * acknowledged (df-ed6d17c8ec6f).
 *
 * `changeIso` is set on every own-device local write (a sync-pulled echo does not
 * set it — the changes feed's own-rev guard filters those out). `pushAckIso` is
 * set ONLY when a push batch actually reaches the server. A pull ack does NOT
 * count: a pull moves no local write off-device, which is exactly why the existing
 * lastUserDbSyncTime_utc flag cannot tell a stuck push apart from a healthy one.
 *
 * Unacked means a change exists and either no push has acked this session or the
 * last push ack predates the change. A missing or unparseable change stamp is
 * "nothing to flush"; an unparseable push stamp counts as "no ack".
 */
export function isLocalWriteUnackedByPush(
  changeIso: string | undefined,
  pushAckIso: string | undefined,
): boolean {
  if (!changeIso) return false;
  const changeMs = DateTime.fromISO(changeIso).toMillis();
  if (!Number.isFinite(changeMs)) return false;
  const pushAckMs = pushAckIso ? DateTime.fromISO(pushAckIso).toMillis() : NaN;
  return !Number.isFinite(pushAckMs) || pushAckMs < changeMs;
}

/** Inputs to {@link decidePushStallReport}. */
export interface PushStallInputs {
  /** the user DB has an own-device write no push has acked ({@link isLocalWriteUnackedByPush}) */
  hasUnackedLocalWrite: boolean;
  /**
   * the environment is one where a stuck push is genuinely anomalous: the app is
   * online, foregrounded, the database is not already known Offline, and no
   * network-shaped sync error happened recently. When this is false the stuck
   * clock is cleared, so the window only ever accumulates over a continuously
   * healthy stretch.
   */
  environmentHealthy: boolean;
  /** epoch ms when the stuck-while-healthy condition was first seen this session,
   *  or null when the clock is not running */
  pushStuckSinceMs: number | null;
  nowMs: number;
  minStallMs: number;
  /** true once this session has already reported the stall (report at most once) */
  alreadyReported: boolean;
}

/** What {@link decidePushStallReport} concluded. */
export interface PushStallDecision {
  /** fire the once-per-session problem report now */
  report: boolean;
  /** value to persist as the stuck-since clock; null clears it */
  nextStuckSinceMs: number | null;
}

/**
 * Decide whether a persistently-failing live-sync push should be reported now
 * (df-ed6d17c8ec6f, approved option A).
 *
 * The clock runs ONLY while there is an unacked local write AND the environment is
 * healthy; either failing clears it. So the window it measures is a CONTINUOUS
 * online-and-foregrounded stretch during which the push never landed — an ordinary
 * offline period clears the clock, and a genuine stall has to re-accumulate the
 * full window. Reports at most once per session.
 *
 * Known limitation: the clock is only sampled when a clean `paused` event fires,
 * so an offline blip that both starts and fully recovers between two paused events
 * is not observed. In practice a failing push retries constantly, so paused events
 * are frequent and a real multi-minute offline period is seen by at least one of
 * them; the 30s network-error tail ({@link isRecentNetworkSyncError}) covers the
 * network-drop case regardless.
 */
export function decidePushStallReport(inputs: PushStallInputs): PushStallDecision {
  const { hasUnackedLocalWrite, environmentHealthy, pushStuckSinceMs, nowMs, minStallMs, alreadyReported } = inputs;
  // if there is nothing waiting to flush, or the environment is not one where a
  // stuck push is anomalous, the clock does not run
  if (!hasUnackedLocalWrite || !environmentHealthy) {
    return { report: false, nextStuckSinceMs: null };
  }
  // start the clock the first time the stuck-while-healthy condition is seen
  const stuckSince = pushStuckSinceMs ?? nowMs;
  const report = !alreadyReported && nowMs - stuckSince >= minStallMs;
  return { report, nextStuckSinceMs: stuckSince };
}

/**
 * Outcome of the pull-side resume catch-up decision (#3862). Each `Skip*` value names the
 * reason so callers can log it; only `Run` triggers the catch-up.
 */
export enum PullCatchupDecision {
  Run = 'run',
  SkipNotSteadyState = 'skip-not-steady-state',
  SkipCooldown = 'skip-cooldown',
  SkipNotOnline = 'skip-not-online',
}

/** Inputs to {@link decidePullCatchup}. All times are epoch milliseconds. */
export interface PullCatchupInputs {
  /** True only once the initial sync has completed — i.e. steady-state live sync. */
  initialSyncCompleted: boolean | undefined;
  /** True when a sync handler is currently running (DatabaseWrapper.syncHandler != null). */
  hasLiveSyncHandler: boolean;
  /** True when the running handler is an initial (one-shot) sync rather than the live one. */
  syncHandlerIsInitial: boolean;
  /** True when the app's connection state is Online. */
  connectionStateIsOnline: boolean;
  /** Epoch ms of the last catch-up run, or null if it has never run this session. */
  lastCatchupMs: number | null;
  /** Current epoch ms. */
  nowMs: number;
  /** Minimum gap between catch-up runs, so rapid resume cycles don't thrash. */
  cooldownMs: number;
}

/**
 * Decide whether a pull-side resume catch-up should run now (#3862).
 *
 * On mobile the live `db.sync` PULL longpoll can die silently across background /
 * network-suspend cycles: no `error`/`denied`/`'Load failed'` event fires, so none of the
 * auto-restart handlers trigger, and the device stops receiving other devices' changes while
 * its own pushes keep working (push is driven by the local changes feed). The resume/online
 * path's `ensureSyncActive()` deliberately SKIPS a sync that *looks* healthy (handler present,
 * state `Ready`, `initialSyncCompleted === true`, not stranded) — which is exactly the
 * silently-dead-pull state.
 *
 * This decision is the INVERSE of that skip: being in steady-state live sync is the
 * PRECONDITION to run a checkpoint-independent catch-up, not a reason to skip. The catch-up
 * only makes sense when a live (not initial) sync is supposedly running and the initial sync
 * has completed; while online; and only after the cooldown has elapsed since the last run.
 */
export function decidePullCatchup(inputs: PullCatchupInputs): PullCatchupDecision {
  if (!inputs.connectionStateIsOnline) return PullCatchupDecision.SkipNotOnline;
  // steady-state live sync is the precondition to run: the initial sync must be done AND a
  // live — not initial — handler must be running. (An in-progress initial sync already pulls
  // everything, and a missing handler is ensureSyncActive's job to start.)
  if (inputs.initialSyncCompleted !== true || !inputs.hasLiveSyncHandler || inputs.syncHandlerIsInitial) {
    return PullCatchupDecision.SkipNotSteadyState;
  }
  // cooldown: don't re-run on rapid background/foreground cycles
  if (inputs.lastCatchupMs != null && inputs.nowMs - inputs.lastCatchupMs < inputs.cooldownMs) {
    return PullCatchupDecision.SkipCooldown;
  }
  return PullCatchupDecision.Run;
}

// #3987 A2 (audit defect D3) — the real-time fast-lane listener used to die
// silently: its error handler was empty, so one failed longpoll ended the
// fast lane for the whole session with no log, no telemetry, no restart.

/**
 * Maximum automatic restarts of the real-time listener per sync session.
 * Bounded so a persistently-broken feed (e.g. auth revoked) cannot loop
 * forever — after the cap the listener stays down and the once-per-session
 * Sentry signal has already told us. The counter resets when a new sync
 * session starts the listener fresh (endInitialSync).
 */
export const REALTIME_LISTENER_MAX_RESTARTS = 3;

/** True while an errored real-time listener may schedule another automatic
 *  restart (#3987 A2). Pure so the cap semantics are unit-testable. */
export function shouldRestartRealtimeListener(restartsSoFar: number): boolean {
  return restartsSoFar < REALTIME_LISTENER_MAX_RESTARTS;
}

// #3987 A5 (audit defect D5) — a session that stays continuously foregrounded
// gets no resume/online event, so the resume-path health checks (push drift +
// pull catch-up) never run and a silently-dead pull longpoll is never caught.
// MyApp arms a repeating foreground tick; this predicate is its run/skip
// decision, kept pure so it is unit-testable.

/** Decide whether the foreground sync-health tick should run its SYNC checks now
 *  (#3987 A5). Runs only when the app is visible, online, and signed in.
 *
 *  A `false` answer means there is no sync work to do — it does NOT mean the tick
 *  does nothing. On the offline branch it goes and checks the connection instead;
 *  `shouldReprobeConnectivityOnTick` below decides that. Either way the caller
 *  re-arms the timer. */
export function shouldRunSyncHealthTick(
  documentHidden: boolean,
  connectionStateIsOnline: boolean,
  hasUser: boolean,
): boolean {
  return !documentHidden && connectionStateIsOnline && hasUser;
}

/**
 * Decide whether the foreground tick should go and CHECK THE CONNECTION now, on a tick where
 * `shouldRunSyncHealthTick` above said there was no sync work to do (df-eabc7656368c).
 *
 * The problem this answers. The app can end up believing it is offline on a device whose network
 * is perfectly fine — one failed check is enough, and on Android a check made while the app was
 * asleep always fails, because a backgrounded WebView is not allowed to use the network at all.
 * Once that belief is in place, the only things that can correct it are a browser online event, a
 * return to the app from the background, a request failing somewhere else, or a feature that is
 * about to refuse the user re-checking first. A person who simply keeps the app open triggers none
 * of those, so the app used to sit on the wrong answer — showing "Offline" and not syncing — for
 * as long as they kept looking at it. A real customer hit exactly that (#4318, and see
 * resume-probe-gate.ts, which fixed the return-from-background half of it).
 *
 * So on a tick that has no sync work to do, we ask the server whether it is reachable after all.
 * Each condition below rules out a case where asking would be pointless or actively harmful:
 */
export function shouldReprobeConnectivityOnTick(input: {
  /** Is the page in a background tab? */
  documentHidden: boolean;
  /** What the app believes right now — `connectionState === ConnectionState.Online`. */
  connectionStateIsOnline: boolean;
  /** Did the browser flatly say there is no network (`navigator.onLine === false`)? */
  browserSaysNoNetwork: boolean;
  /** Is anybody signed in? */
  hasUser: boolean;
}): boolean {
  // A hidden page is the one place a check MANUFACTURES the very wrong answer this is meant to
  // repair: on Android a backgrounded WebView cannot reach the network, so the check fails within
  // milliseconds and reports "offline" about the app being asleep rather than about the phone.
  if (input.documentHidden) return false;

  // Nobody is signed in, so there is no sync to recover and no offline pill over anyone's data.
  // The sign-in path does its own connection checking when somebody tries to log in.
  if (!input.hasUser) return false;

  // We already believe we are online. An "online" belief that has gone stale is not worth a timer:
  // the next request that fails will say so, and it is not the belief that hurts anyone when it is
  // wrong. resume-probe-gate.ts makes the same distinction for the same reason.
  if (input.connectionStateIsOnline) return false;

  // The browser says there is no network at all. That is the ONE direction its flag can be trusted
  // (`true` only means "some interface is up"), so the answer is already known and a check would
  // buy nothing — see decideRefusalRecheck in refusal-recheck-gate.ts, which uses the same rule.
  // This is also what keeps a genuinely disconnected phone from spending anything at all here.
  if (input.browserSaysNoNetwork) return false;

  return true;
}

/** A remote doc as seen by the pull catch-up (#3862) — only `_id`/`_rev` are needed to decide. */
export interface PullCatchupRemoteDoc {
  _id: string;
  _rev: string;
}

/**
 * A local `allDocs({ keys })` row. A key missing locally returns a row with `error` and no
 * `value`/`id` (only `key` is always present), so callers must key by `key`, not `id`.
 */
export interface PullCatchupLocalRow {
  key: string;
  value?: { rev?: string } | null;
  error?: any;
}

/** One selected missed doc (#3987 A4). `sameGen` is true ONLY for the
 *  same-generation-different-hash case (e.g. local `3-def` vs remote `3-abc`),
 *  so the caller can apply the convergence guard to exactly that subset —
 *  absent-locally and newer-generation entries keep the existing fast path. */
export interface MissedDocEntry<T> {
  doc: T;
  sameGen: boolean;
}

/**
 * Select the remote docs the local DB is missing or behind on (#3862, pull catch-up). A doc is
 * "missed" when it is absent locally, its remote rev generation is greater than the local
 * one, or (#3987 A4, audit defect D6) the generations are EQUAL but the rev hashes differ —
 * two devices each wrote generation N independently and the leaves diverged. The old
 * generation-only comparison could not see that last case at all. Keys the local rows by
 * `row.key` (always present) and treats a row with no `value` as absent — `row.id` is
 * undefined for a not-found key, so keying by it would silently drop misses.
 */
export function selectMissedDocs<T extends PullCatchupRemoteDoc>(
  remoteDocs: T[],
  localRows: PullCatchupLocalRow[],
): MissedDocEntry<T>[] {
  const localRevByKey = new Map<string, string | null>();
  for (const row of localRows) {
    localRevByKey.set(row.key, row.value?.rev ?? null);
  }
  const missed: MissedDocEntry<T>[] = [];
  for (const doc of remoteDocs) {
    const localRev = localRevByKey.get(doc._id) ?? null;
    // if the doc is absent locally, or the server's generation is newer, we missed it
    if (!localRev || parseRevGeneration(doc._rev) > parseRevGeneration(localRev)) {
      missed.push({ doc, sameGen: false });
    } else if (parseRevGeneration(doc._rev) === parseRevGeneration(localRev) && doc._rev !== localRev) {
      // same generation, different hash: local and remote hold divergent leaves (D6)
      missed.push({ doc, sameGen: true });
    }
  }
  return missed;
}

/**
 * What one ensurePullCatchupAsync invocation actually did (O5, #4027 Step 25) —
 * consumed by the soft-resume `resume_recovery` timing event. `ran: false`
 * carries the skip reason (cooldown, guard, error); a run carries the
 * counters: how many priority-window docs were rev-checked, how many were
 * actually missed (bodies fetched + grafted), and the approximate JSON size
 * of the fetched bodies — the payload the rev-first split saves on healthy
 * resumes.
 */
export interface PullCatchupStats {
  ran: boolean;
  skipReason?: string;
  durationMs?: number;
  checkedCount?: number;
  missedCount?: number;
  bodyBytes?: number;
}
